/* "Fix SQL": the statements a steward runs, at their own source, to find and fix one violation's rows.
 *
 * **Lumiq runs none of it and never sees what it returns, and the panel says so in words.** A steward
 * handed SQL with no explanation will reasonably wonder why the screen could not simply fix the rows;
 * the answer is that the rows live in their database and stay there. Reconciliation's Investigate
 * panel is the precedent, and this one keeps its shape — a statement per block, copy per block, one
 * download for the lot.
 *
 * **A template is marked before anybody copies it.** `needs_value` comes from the server and is the
 * only thing that decides the warning: the statement carries a `REPLACE` placeholder where the value
 * belongs, and the warning says to write the value in, because Lumiq never invents one.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useViolationResolution } from '../api/rules';
import type { ResolutionStatement, ViolationResolution } from '../api/rules';
import {
  FIX_KIND_KEY,
  KIND_NOTE,
  resolutionToSql,
  statementTitleKey,
} from '../model/resolution';

export function ResolutionSqlPanel({
  violationId,
  onClose,
}: {
  /** `undefined` closes the panel; the query runs only while it is open. */
  readonly violationId: string | undefined;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const open = violationId !== undefined;
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title={t('resolution.title')}
      hint={t('resolution.hint')}
    >
      {open && <ResolutionBody violationId={violationId} />}
      <p className="lq-note">{t('resolution.foot')}</p>
    </Modal>
  );
}

function ResolutionBody({ violationId }: { readonly violationId: string }): ReactElement {
  const { t } = useTranslation();
  const resolution = useViolationResolution(violationId);
  const [copied, setCopied] = useState<number | null>(null);

  if (resolution.isPending) return <SkeletonRows rows={4} columns={1} />;
  if (resolution.isError) {
    /* The server's own sentence, verbatim — "a custom_sql rule's violation is whatever its own
       statement counts" names the reason, which a paraphrase would lose. */
    return (
      <Alert tone="error" title={t('resolution.failed')}>
        {resolution.error.message}
      </Alert>
    );
  }
  const data = resolution.data;
  const titleOf = (statement: ResolutionStatement): string =>
    t(statementTitleKey(statement, data.rule_kind), { column: statement.column ?? '' });

  async function copy(index: number, sql: string): Promise<void> {
    await navigator.clipboard.writeText(sql);
    setCopied(index);
  }

  return (
    <section aria-label={t('resolution.title')}>
      <p className="lq-rs__where">
        {t('resolution.where', {
          rule: data.rule_name,
          engine: data.engine,
          connection: data.connection_name ?? t('resolution.noConnection'),
        })}{' '}
        <span className="lq-mono">{data.relation}</span>
      </p>
      <div className={`lq-band lq-band--${data.fix_kind === 'none' ? 'info' : 'warn'}`}>
        <div className="lq-band__body">{t(FIX_KIND_KEY[data.fix_kind])}</div>
      </div>

      {data.statements.map((statement, index) => (
        <section
          key={`${statement.purpose}-${String(index)}`}
          className="lq-rs__statement"
        >
          <div className="lq-rs__head">
            <span className="lq-rs__step">{String(index + 1)}</span>
            <span className="lq-rs__title">{titleOf(statement)}</span>
          </div>
          {statement.needs_value && (
            <p className="lq-rs__needs" role="note">
              {t('resolution.needsValue')}
            </p>
          )}
          {statement.bounded && (
            <p className="lq-rs__limit">
              {t('resolution.limit', { count: data.row_limit })}
            </p>
          )}
          {/* `<pre>` with the SQL as a child, so nothing interprets it. */}
          <pre className="lq-mono lq-rs__code" tabIndex={0}>
            {statement.sql}
          </pre>
          <button
            type="button"
            className="lq-sect__ghost"
            onClick={() => {
              void copy(index, statement.sql);
            }}
          >
            {copied === index ? t('resolution.copied') : t('resolution.copy')}
          </button>
        </section>
      ))}

      {KIND_NOTE[data.rule_kind] !== undefined && (
        <p className="lq-rs__kindNote">{t(KIND_NOTE[data.rule_kind] ?? '')}</p>
      )}
      <div className="lq-band lq-band--info">
        <div className="lq-band__body">{t('resolution.after')}</div>
      </div>

      <div className="lq-rs__actions">
        <button
          type="button"
          className="lq-sect__ghost"
          onClick={() => {
            downloadSql(
              fileName(data),
              resolutionToSql(data, t('resolution.banner'), titleOf),
            );
          }}
        >
          {t('resolution.download')}
        </button>
      </div>
    </section>
  );
}

function fileName(resolution: ViolationResolution): string {
  return `${resolution.rule_kind}-${resolution.violation_id}-fix.sql`;
}

/* A plain-text download, revoked in the same turn: the click has already been dispatched, so the blob
 * has served its purpose. The reconciliation panel's helper, at a second site. */
function downloadSql(filename: string, body: string): void {
  const blob = new Blob([body], { type: 'application/sql;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
